import { useState, useRef } from "react";
import { SavedAttachmentInfo } from "@/entities/attachments/types";

interface UseAttachmentsProps {
  initialAttachments?: SavedAttachmentInfo[] | null;
  onAttachmentsChange?: (attachments: SavedAttachmentInfo[]) => void;
}

export const useAttachments = ({
  initialAttachments,
  onAttachmentsChange
}: UseAttachmentsProps) => {
  // Stored activities carry `attachments: null`, which a destructuring
  // default would not replace.
  const [attachments, setAttachments] = useState<SavedAttachmentInfo[]>(
    initialAttachments ?? []
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const filesArray = Array.from(files);

      // Create simple attachment objects for display
      const newAttachments: SavedAttachmentInfo[] = filesArray.map(
        (file, index) => ({
          id: `${Date.now()}-${index}`,
          filename: file.name
        })
      );

      const updatedAttachments = [...attachments, ...newAttachments];
      setAttachments(updatedAttachments);
      onAttachmentsChange?.(updatedAttachments);
    }
    // Reset the input value so the same file can be selected again
    e.target.value = "";
  };

  const removeAttachment = (attachmentId: string) => {
    const updatedAttachments = attachments.filter(
      (att) => att.id !== attachmentId
    );
    setAttachments(updatedAttachments);
    onAttachmentsChange?.(updatedAttachments);
  };

  return {
    attachments,
    fileInputRef,
    handleFileUploadClick,
    handleFileSelect,
    removeAttachment
  };
};
